import { yupResolver } from "@hookform/resolvers/yup";
import { FC, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import {
  FormController,
  MuiBox,
  MuiGrid,
  MuiModal,
  MuiSelect,
  MuiTextField,
} from "../../../../../../components";
import {
  DOCUMENT_SITE_CORE_KEY,
  PAGE_IDS,
} from "../../../../../../helpers/constants";
import { replaceHashWithValue } from "../../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import {
  CancelContractDocumentFormKey,
  ClientDocument,
} from "../../../../modelsAndSchemas/Models";
import { CancelContractDocumentModalValidationSchema } from "../../../../modelsAndSchemas/ValidationSchema";
import { documentSiteCoreKeys } from "../../../../siteCore/SiteCoreKeys";
import { CancelContractDocumentModalsWrapper } from "./CancelContractDocumentModal.style";

interface CancelContractDocumentModalProps {
  documentData?: ClientDocument;
  open: boolean;
  onClose: () => void;
  onConfirm: (cancellationReason: string, comment?: string) => void;
}

const CancelContractDocumentModal: FC<CancelContractDocumentModalProps> = ({
  documentData,
  open,
  onClose,
  onConfirm,
}) => {
  const {
    fetchLabel,
    commonLabels,
    fetchValidationMessage,
    fetchDropdownValues,
  } = useSiteCore();

  const { commonARIALabel } = useNavigationSiteCore();

  const methods = useForm({
    mode: "onBlur",
    defaultValues: {
      [CancelContractDocumentFormKey.cancellationReason]: "",
      [CancelContractDocumentFormKey.comment]: "",
    },
    resolver: yupResolver(
      CancelContractDocumentModalValidationSchema(fetchValidationMessage),
    ),
  });

  const { control, getValues, handleSubmit } = methods;
  const [showCommentField, setShowCommentField] = useState(false);

  const pageId = PAGE_IDS.CANCEL_CONTRACT_MODAL;
  const gridSize = { lg: 4, md: 6, xs: 12, sm: 6 };

  const { documentsTitle } = documentSiteCoreKeys;

  const getLabel = (key: keyof typeof documentSiteCoreKeys) => {
    return fetchLabel(documentsTitle, documentSiteCoreKeys[key]);
  };

  const getDropdownList = (key: keyof typeof documentSiteCoreKeys) => {
    return fetchDropdownValues(documentsTitle, documentSiteCoreKeys[key]);
  };

  const handleCancelContractDocumentSubmit = () => {
    const { cancellationReason, comment } = getValues();
    onConfirm(cancellationReason, comment);
  };

  const onChangeCancellationReason = () => {
    const { cancellationReason } = getValues();
    setShowCommentField(
      cancellationReason === DOCUMENT_SITE_CORE_KEY.cancellationReasonOther,
    );
  };

  return (
    <FormProvider {...methods}>
      <MuiModal
        open={open}
        onClose={onClose}
        title={replaceHashWithValue(
          getLabel("cancelContractHeading"),
          documentData?.documentName ?? "",
        )}
        hasLargeContent
        secondaryButtonTitle={commonLabels.cancel}
        primaryButtonTitle={commonLabels.save}
        onSecondaryButtonClick={onClose}
        onPrimaryButtonClick={() =>
          handleSubmit(handleCancelContractDocumentSubmit)()
        }
      >
        <CancelContractDocumentModalsWrapper>
          <MuiBox className="container">
            <MuiGrid container>
              <MuiGrid size={gridSize}>
                <FormController
                  control={control}
                  name={CancelContractDocumentFormKey.cancellationReason}
                >
                  <MuiSelect
                    id={`${pageId}-${CancelContractDocumentFormKey.cancellationReason}`}
                    label={getLabel("cancellationReason")}
                    visibleDefaultSelectOption
                    placeholder={commonLabels.selectPlaceHolder}
                    options={getDropdownList("cancellationReasonList")}
                    onChange={onChangeCancellationReason}
                    isMandatory
                    requiredARIALabel={commonARIALabel.required}
                  />
                </FormController>
              </MuiGrid>
            </MuiGrid>
            {showCommentField && (
              <MuiBox className="cancel-contract-document-comment">
                <FormController
                  control={control}
                  name={CancelContractDocumentFormKey.comment}
                >
                  <MuiTextField
                    placeholder={commonLabels.inputPlaceHolder}
                    id={`${pageId}-${CancelContractDocumentFormKey.comment}`}
                    label={commonLabels.comment}
                    className="comment-input"
                    multiline
                    rows={4}
                    requiredARIALabel={commonARIALabel.required}
                  />
                </FormController>
              </MuiBox>
            )}
          </MuiBox>
          <MuiBox className="separator" />
        </CancelContractDocumentModalsWrapper>
      </MuiModal>
    </FormProvider>
  );
};
export default CancelContractDocumentModal;
